import React, { useEffect, useMemo, useState } from 'react';
import { X, Download, Copy, QrCode, Share2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';

const QRGenerator = ({ onClose, shopData }) => {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  // Compose payload with explicit name/email for scanning
  const payload = useMemo(() => {
    const name = shopData?.shopDetails?.[0]?.shopName || currentUser?.displayName || 'My Shop';
    const email = shopData?.email || currentUser?.email || '';
    return {
      type: 'business',
      name,
      email,
      shopId: shopData?.profile_id || currentUser?.uid || '',
    };
  }, [shopData, currentUser]);

  const shopMeta = useMemo(() => ({
    shopName: shopData?.shopDetails?.[0]?.shopName || payload.name || 'My Shop',
    category: shopData?.shopDetails?.[0]?.category || 'General',
    phone: shopData?.phone || '',
    shopId: payload.shopId || '',
  }), [shopData, payload]);

  const [qrCodeUrl, setQrCodeUrl] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const qrText = `Name: ${payload.name}\nEmail: ${payload.email}`;
        const url = await QRCode.toDataURL(qrText, {
          errorCorrectionLevel: 'M',
          margin: 2,
          width: 256,
        });
        if (mounted) setQrCodeUrl(url);
      } catch (e) {
        console.error('QR generate failed', e);
        toast.error('Failed to generate QR');
      }
    })();
    return () => { mounted = false; };
  }, [payload]);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.download = `${payload.name || 'Shop'}-QR.png`;
    link.href = qrCodeUrl;
    link.click();
    toast.success('QR Code downloaded!');
  };

  const handleCopyData = () => {
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    toast.success('Shop data copied to clipboard!');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${payload.name} - QR Code`,
          text: `Scan this QR code to connect with ${payload.name}`,
          url: window.location.href
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      handleCopyData();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Shop QR Code</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* QR Code Display */}
          <div className="text-center mb-6">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 inline-block mb-4">
              {qrCodeUrl ? (
                <img
                  src={qrCodeUrl}
                  alt="Shop QR Code"
                  className="w-48 h-48 mx-auto"
                />
              ) : (
                <div className="w-48 h-48 mx-auto flex items-center justify-center text-gray-400">
                  <QrCode className="w-10 h-10" />
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600">
              Customers can scan this QR code to view your shop details and offers
            </p>
          </div>

          {/* Shop Information */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-900 mb-3">Shop Information</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Shop Name:</span>
                <span className="font-medium">{shopMeta.shopName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Category:</span>
                <span className="font-medium">{shopMeta.category}</span>
              </div>
              {shopMeta.phone && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-medium">{shopMeta.phone}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Shop ID:</span>
                <span className="font-mono text-xs">{shopMeta.shopId}</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={handleDownload}
              className="flex flex-col items-center space-y-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-5 h-5 text-gray-600" />
              <span className="text-xs text-gray-600">Download</span>
            </button>
            
            <button
              onClick={handleCopyData}
              className="flex flex-col items-center space-y-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Copy className="w-5 h-5 text-gray-600" />
              <span className="text-xs text-gray-600">Copy Data</span>
            </button>
            
            <button
              onClick={handleShare}
              className="flex flex-col items-center space-y-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Share2 className="w-5 h-5 text-gray-600" />
              <span className="text-xs text-gray-600">Share</span>
            </button>
          </div>

          {/* Usage Instructions */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h5 className="font-medium text-blue-900 mb-2">How to use:</h5>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Display this QR code at your shop entrance</li>
              <li>• Customers can scan to view your offers</li>
              <li>• Use the scanner to process customer transactions</li>
              <li>• Download and print for physical display</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRGenerator;
